import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMG } from "@/lib/content";
import { MaskedLines } from "@/components/site/Reveal";
import ConceptTag from "@/components/site/ConceptTag";

const EASE = [0.16, 1, 0.3, 1];

const scrollToConcept = () => {
  const el = document.getElementById("concept");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.8]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.exterior}
          alt="RestPower charging and hospitality destination concept at night"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-rp-bg via-rp-bg/25 to-black/50" />

      <ConceptTag className="absolute right-6 top-24 md:right-12 md:top-28" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-28 md:px-12 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-rp-accent sm:text-xs">
            <span className="h-px w-8 bg-rp-accent" />
            A New Vision for the Roadside Stop
          </span>
        </motion.div>

        <h1 className="max-w-4xl font-display text-[2.75rem] font-light uppercase leading-[0.92] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLines lines={["More Than a Charge."]} lineClassName="text-white" delay={0.15} />
          <MaskedLines lines={["It's a Better Stop."]} lineClassName="text-rp-accent" delay={0.32} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          RestPower is reimagining the roadside stop by bringing ultra-fast EV charging together with
          comfortable lounges, fresh food, clean restrooms, workspaces, entertainment, and travel
          essentials — all in one destination.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.72 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button onClick={scrollToConcept} data-testid="hero-explore-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-rp-accent px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-200 hover:bg-white">
            Explore RestPower
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <Link to="/experience" data-testid="hero-experience-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:border-rp-accent hover:text-rp-accent">
            See the Experience
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-rp-accent" />
      </motion.div>
    </section>
  );
}
